import { useEffect, useState } from "react";
import { Box, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { Field, FormInput, FormSelect, FormSwitch, EmailTagsInput, TagsInput, CollapsibleSection, Grid2, AddButton, RemoveButton, isFieldInvalid, isFieldWarning } from "@/components/jobOffer/ui";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import { CampaignService } from "@/services/CampaignService";
import { LEAD_STATUS_OPTIONS } from "@/components/campaign/leadStatusOptions";
import GoogleSheetsConnectPanel from "@/components/campaign/GoogleSheetsConnectPanel";
import MicrosoftExcelConnectPanel from "@/components/campaign/MicrosoftExcelConnectPanel";

const LEAD_SOURCE_OPTIONS = [
    { value: "GoogleSheets", label: "Google Sheets" },
    { value: "Excel", label: "Microsoft Excel" },
    { value: "Api", label: "API / Webhook" },
    { value: "Manual", label: "Ręcznie" },
];

const STATUS_OPTIONS = [
    { value: "Draft", label: "Wersja robocza" },
    { value: "Active", label: "Aktywna" },
    { value: "Inactive", label: "Wstrzymana" },
];

// Limited to fields that actually exist on the Lead entity (SalesManager.Domain.Leads.Lead) --
// the spec's example table also lists Miejscowość/Produkt/Kampania źródłowa/Zainteresowanie/
// Komentarz, none of which are Lead columns; those go through "Pola dodatkowe" -> AdditionalData.
const SYSTEM_FIELD_OPTIONS = [
    { value: "Firstname", label: "Imię" },
    { value: "Lastname", label: "Nazwisko" },
    { value: "PhoneNumber", label: "Telefon" },
    { value: "Email", label: "E-mail" },
    { value: "Company", label: "Firma" },
    { value: "JobTitle", label: "Stanowisko" },
    { value: "Status", label: "Status" },
    { value: "Source", label: "Źródło" },
];

const NO_MAPPING = "__none__";

const newCustomField = () => ({ fieldName: "", sourceColumn: "" });

const Step2LeadSource = ({ formik, values, setValues, warningErrors, visited, forceOpen }) => {
    const set = (key, val) => setValues({ ...values, [key]: val });
    const invalid = (path) => isFieldInvalid(formik, path);
    const warning = (path) => isFieldWarning(warningErrors, visited, path);

    const leadSource = values.leadSourceConfig ?? {};
    const setLeadSource = (patch) => set("leadSourceConfig", { ...leadSource, ...patch });

    // Editing the URL/tab invalidates any previous check -- it was only ever verified for the old value.
    // Also drop leadFieldMappings: they reference column names from the previous sheet, which would
    // otherwise keep hiding those system fields as "taken" for the new sheet's columns.
    const setSheetField = (patch) => {
        setValues({
            ...values,
            leadSourceConfig: { ...leadSource, ...patch, isConnected: false },
            leadFieldMappings: [],
        });
        setColumns(null);
    };

    const [serviceAccountEmail, setServiceAccountEmail] = useState(null);
    const [excelConnectedAccountEmail, setExcelConnectedAccountEmail] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const [columns, setColumns] = useState(null);

    const isGoogleSheets = values.leadSourceType === "GoogleSheets";
    const isExcel = values.leadSourceType === "Excel";

    useEffect(() => {
        if (!isGoogleSheets || serviceAccountEmail) return;
        CampaignService.getLeadSourceServiceAccountEmail()
            .then((data) => setServiceAccountEmail(data?.email ?? null))
            .catch(() => {});
    }, [isGoogleSheets, serviceAccountEmail]);

    useEffect(() => {
        if (!isGoogleSheets && !isExcel) setColumns(null);
    }, [isGoogleSheets, isExcel]);

    useEffect(() => {
        if (isGoogleSheets && leadSource.isConnected && leadSource.spreadsheetUrl && leadSource.sheetName) {
            runCheck().then((result) => { if (result) setColumns(result); });
        } else if (isExcel && leadSource.isConnected && leadSource.excelFileUrl && leadSource.excelWorksheetName && leadSource.excelConnectionId) {
            runExcelCheck().then((result) => { if (result) setColumns(result); });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkAccess = async (spreadsheetUrl, sheetName) => {
        try {
            const data = await CampaignService.checkLeadSourceAccess(spreadsheetUrl, sheetName);
            return { isConnected: !!data?.isConnected, columns: data?.columns ?? [], error: data?.error ?? null };
        } catch (error) {
            return { isConnected: false, columns: [], error: extractBusinessErrorMessage(error) ?? "Nie udało się zweryfikować dostępu." };
        }
    };

    const checkExcelAccess = async (fileUrl, worksheetName, connectionId) => {
        try {
            const data = await CampaignService.checkExcelLeadSourceAccess(fileUrl, worksheetName, connectionId);
            return { isConnected: !!data?.isConnected, columns: data?.columns ?? [], error: data?.error ?? null };
        } catch (error) {
            return { isConnected: false, columns: [], error: extractBusinessErrorMessage(error) ?? "Nie udało się zweryfikować dostępu." };
        }
    };

    const runCheck = async () => {
        if (!leadSource.spreadsheetUrl?.trim() || !leadSource.sheetName?.trim()) return null;
        setIsChecking(true);
        try {
            const result = await checkAccess(leadSource.spreadsheetUrl, leadSource.sheetName);
            return result.isConnected ? result.columns : null;
        } finally {
            setIsChecking(false);
        }
    };

    const runExcelCheck = async () => {
        if (!leadSource.excelFileUrl?.trim() || !leadSource.excelWorksheetName?.trim() || !leadSource.excelConnectionId) return null;
        setIsChecking(true);
        try {
            const result = await checkExcelAccess(leadSource.excelFileUrl, leadSource.excelWorksheetName, leadSource.excelConnectionId);
            return result.isConnected ? result.columns : null;
        } finally {
            setIsChecking(false);
        }
    };

    const handleRefreshColumns = async () => {
        const result = isExcel ? await runExcelCheck() : await runCheck();
        if (result) setColumns(result);
    };

    const handleExcelAccountConnected = ({ connectionId, connectedAccountEmail }) => {
        setLeadSource({ excelConnectionId: connectionId, isConnected: false });
        setExcelConnectedAccountEmail(connectedAccountEmail ?? null);
        setColumns(null);
    };

    // --- Field mapping (system fields) ---
    const leadFieldMappings = values.leadFieldMappings ?? [];
    const systemFieldFor = (sourceColumn) => leadFieldMappings.find(m => m.sourceColumn === sourceColumn)?.systemField ?? "";
    const setSystemFieldFor = (sourceColumn, systemField) => {
        const rest = leadFieldMappings.filter(m => m.sourceColumn !== sourceColumn);
        set("leadFieldMappings", systemField ? [...rest, { systemField, sourceColumn }] : rest);
    };
    const takenSystemFields = new Set(leadFieldMappings.map(m => m.systemField));

    const columnFor = (systemField) => leadFieldMappings.find(m => m.systemField === systemField)?.sourceColumn ?? "";
    const setColumnFor = (systemField, sourceColumn) => {
        const rest = leadFieldMappings.filter(m => m.systemField !== systemField);
        set("leadFieldMappings", sourceColumn ? [...rest, { systemField, sourceColumn }] : rest);
    };

    // --- Custom fields ---
    const customFields = values.customFieldMappings ?? [];
    const setCustomFields = (next) => set("customFieldMappings", next);
    const updateCustomField = (i, patch) => setCustomFields(customFields.map((c, idx) => idx === i ? { ...c, ...patch } : c));
    const removeCustomField = (i) => setCustomFields(customFields.filter((_, idx) => idx !== i));
    const addCustomField = () => setCustomFields([...customFields, newCustomField()]);

    const hasRealColumns = Array.isArray(columns) && columns.length > 0;

    // --- Decision column ---
    const [decisionColumnValues, setDecisionColumnValues] = useState([]);
    const [isLoadingDecisionValues, setIsLoadingDecisionValues] = useState(false);

    const fetchSourceColumnValues = async (columnName) => {
        if (isExcel) {
            if (!columnName || !leadSource.excelFileUrl || !leadSource.excelWorksheetName || !leadSource.excelConnectionId) return [];
            const data = await CampaignService.getExcelLeadSourceColumnValues(
                leadSource.excelFileUrl, leadSource.excelWorksheetName, leadSource.excelConnectionId, columnName);
            return data?.values ?? [];
        }
        if (!columnName || !leadSource.spreadsheetUrl || !leadSource.sheetName) return [];
        const data = await CampaignService.getLeadSourceColumnValues(leadSource.spreadsheetUrl, leadSource.sheetName, columnName);
        return data?.values ?? [];
    };

    const fetchDecisionColumnValues = async (columnName) => {
        setIsLoadingDecisionValues(true);
        try {
            setDecisionColumnValues(await fetchSourceColumnValues(columnName));
        } catch {
            setDecisionColumnValues([]);
        } finally {
            setIsLoadingDecisionValues(false);
        }
    };

    useEffect(() => {
        if (hasRealColumns && leadSource.decisionColumn) fetchDecisionColumnValues(leadSource.decisionColumn);
        else setDecisionColumnValues([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadSource.decisionColumn, hasRealColumns]);

    const setDecisionColumn = (columnName) => {
        setLeadSource({ decisionColumn: columnName || null, decisionColumnAllowedValues: [] });
    };

    // --- Status column ---
    const [statusColumnValues, setStatusColumnValues] = useState([]);
    const [isLoadingStatusValues, setIsLoadingStatusValues] = useState(false);

    const fetchStatusColumnValues = async (columnName) => {
        setIsLoadingStatusValues(true);
        try {
            setStatusColumnValues(await fetchSourceColumnValues(columnName));
        } catch {
            setStatusColumnValues([]);
        } finally {
            setIsLoadingStatusValues(false);
        }
    };

    useEffect(() => {
        if (hasRealColumns && leadSource.statusColumn) fetchStatusColumnValues(leadSource.statusColumn);
        else setStatusColumnValues([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadSource.statusColumn, hasRealColumns]);

    const setStatusColumn = (columnName) => {
        setLeadSource({ statusColumn: columnName || null, statusValueMapping: [], defaultStatus: null });
    };

    const statusValueMapping = leadSource.statusValueMapping ?? [];
    const statusFor = (sourceValue) => statusValueMapping.find(m => m.sourceValue === sourceValue)?.status ?? "";
    const setStatusFor = (sourceValue, status) => {
        const rest = statusValueMapping.filter(m => m.sourceValue !== sourceValue);
        setLeadSource({ statusValueMapping: status ? [...rest, { sourceValue, status }] : rest });
    };

    return (
        <VStack w="full" align="stretch" gap={4}>
            <CollapsibleSection title="Powiadomienia i źródło leadów" defaultOpen forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    <FormSwitch
                        label="Wysyłaj powiadomienia o nowym leadzie"
                        checked={!!values.sendNotifications}
                        onChange={(v) => set("sendNotifications", v)}
                    />
                    {values.sendNotifications && (
                        <Field label="Adresy e-mail do powiadomień" required>
                            <EmailTagsInput values={values.notificationEmails ?? []} onChange={(v) => set("notificationEmails", v)} placeholder="adres@firma.pl" />
                        </Field>
                    )}
                    <Grid2>
                        <Field label="Źródło leadów">
                            <FormSelect value={values.leadSourceType ?? ""} onChange={(v) => set("leadSourceType", v || null)} options={LEAD_SOURCE_OPTIONS} placeholder="Wybierz źródło" />
                        </Field>
                        <Field label="Status kampanii" required>
                            <FormSelect value={values.status ?? "Draft"} onChange={(v) => set("status", v)} options={STATUS_OPTIONS} placeholder="Wybierz status" />
                        </Field>
                    </Grid2>
                    {values.leadSourceType === "GoogleSheets" && (
                        <VStack align="stretch" gap={4}>
                            <Grid2>
                                <Field label="URL arkusza Google Sheets" required invalid={invalid("leadSourceConfig.spreadsheetUrl")} warning={warning("leadSourceConfig.spreadsheetUrl")}>
                                    <FormInput
                                        value={leadSource.spreadsheetUrl ?? ""}
                                        onChange={(e) => setSheetField({ spreadsheetUrl: e.target.value })}
                                        placeholder="https://docs.google.com/spreadsheets/d/..."
                                    />
                                </Field>
                                <Field label="Nazwa zakładki (arkusz)" required invalid={invalid("leadSourceConfig.sheetName")} warning={warning("leadSourceConfig.sheetName")}>
                                    <FormInput
                                        value={leadSource.sheetName ?? ""}
                                        onChange={(e) => setSheetField({ sheetName: e.target.value })}
                                        placeholder="np. Leady"
                                    />
                                </Field>
                            </Grid2>
                            <GoogleSheetsConnectPanel
                                serviceAccountEmail={serviceAccountEmail}
                                spreadsheetUrl={leadSource.spreadsheetUrl}
                                sheetName={leadSource.sheetName}
                                isConnected={!!leadSource.isConnected}
                                onCheckAccess={checkAccess}
                                onConnected={(cols) => { setLeadSource({ isConnected: true }); setColumns(cols); }}
                            />
                        </VStack>
                    )}
                    {isExcel && (
                        <VStack align="stretch" gap={4}>
                            <Grid2>
                                <Field label="Link do pliku Excel" required invalid={invalid("leadSourceConfig.excelFileUrl")} warning={warning("leadSourceConfig.excelFileUrl")}>
                                    <FormInput
                                        value={leadSource.excelFileUrl ?? ""}
                                        onChange={(e) => setSheetField({ excelFileUrl: e.target.value })}
                                        placeholder="https://onedrive.live.com/... lub https://firma.sharepoint.com/..."
                                    />
                                </Field>
                                <Field label="Nazwa arkusza (worksheet)" required invalid={invalid("leadSourceConfig.excelWorksheetName")} warning={warning("leadSourceConfig.excelWorksheetName")}>
                                    <FormInput
                                        value={leadSource.excelWorksheetName ?? ""}
                                        onChange={(e) => setSheetField({ excelWorksheetName: e.target.value })}
                                        placeholder="np. Leady"
                                    />
                                </Field>
                            </Grid2>
                            <MicrosoftExcelConnectPanel
                                campaignId={values.id}
                                purpose="LeadSource"
                                fileUrl={leadSource.excelFileUrl}
                                worksheetName={leadSource.excelWorksheetName}
                                connectionId={leadSource.excelConnectionId}
                                connectedAccountEmail={excelConnectedAccountEmail}
                                isConnected={!!leadSource.isConnected}
                                onCheckAccess={checkExcelAccess}
                                onAccountConnected={handleExcelAccountConnected}
                                onConnected={(cols) => { setLeadSource({ isConnected: true }); setColumns(cols); }}
                            />
                        </VStack>
                    )}
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection
                title="Mapowanie pól"
                subtitle={hasRealColumns
                    ? "Które pole systemowe odpowiada każdej odczytanej kolumnie arkusza"
                    : "Które kolumny w źródle danych odpowiadają polom leada"}
                defaultOpen
                forceOpen={forceOpen}
            >
                <VStack align="stretch" gap={3}>
                    {(isGoogleSheets || isExcel) && (
                        <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
                            <Text fontSize="xs" color="text.grey">
                                {leadSource.isConnected
                                    ? (hasRealColumns ? `Odczytano ${columns.length} kolumn z arkusza.` : "Brak odczytanych kolumn.")
                                    : `Sprawdź dostęp i kliknij „Użyj tego ${isExcel ? "pliku" : "arkusza"}”, żeby zmapować kolumny automatycznie.`}
                            </Text>
                            {leadSource.isConnected && (
                                <Box
                                    as="button" type="button" onClick={handleRefreshColumns} disabled={isChecking}
                                    px={3} py={1.5} borderRadius={6} fontSize="xs"
                                    bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                                    opacity={isChecking ? 0.6 : 1}
                                    display="flex" alignItems="center" gap={2}
                                >
                                    {isChecking && <Spinner size="xs" />}
                                    Odśwież kolumny
                                </Box>
                            )}
                        </HStack>
                    )}

                    {hasRealColumns ? (
                        columns.map((col) => {
                            const currentValue = systemFieldFor(col);
                            const options = [
                                { value: NO_MAPPING, label: "— nie mapuj —" },
                                ...SYSTEM_FIELD_OPTIONS.filter(o => o.value === currentValue || !takenSystemFields.has(o.value)),
                            ];
                            return (
                                <Box key={col} display="flex" alignItems="center" gap={4}>
                                    <Text fontSize="sm" color="text.white" flex={1} minW={0} title={col} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{col}</Text>
                                    <Box flex={1}>
                                        <FormSelect
                                            value={currentValue || NO_MAPPING}
                                            onChange={(v) => setSystemFieldFor(col, v === NO_MAPPING ? "" : v)}
                                            options={options}
                                            placeholder="— nie mapuj —"
                                        />
                                    </Box>
                                </Box>
                            );
                        })
                    ) : (
                        SYSTEM_FIELD_OPTIONS.map(({ value, label }) => (
                            <Box key={value} display="flex" alignItems="center" gap={4}>
                                <Text fontSize="sm" color="text.white" w="140px" flexShrink={0}>{label}</Text>
                                <Box flex={1}>
                                    <FormInput value={columnFor(value)} onChange={(e) => setColumnFor(value, e.target.value)} placeholder="np. first_name" />
                                </Box>
                            </Box>
                        ))
                    )}

                    <Box borderTopWidth={1} borderColor="border.primary" pt={3} mt={1}>
                        <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                            Pola dodatkowe (niestandardowe)
                        </Text>
                        <VStack align="stretch" gap={3}>
                            {customFields.map((c, i) => (
                                <Box key={i} display="flex" gap={2} alignItems="flex-end">
                                    <Field label="Nazwa pola">
                                        <FormInput value={c.fieldName} onChange={(e) => updateCustomField(i, { fieldName: e.target.value })} placeholder='np. "zainteresowanie"' />
                                    </Field>
                                    <Field label="Kolumna w źródle">
                                        <FormInput value={c.sourceColumn} onChange={(e) => updateCustomField(i, { sourceColumn: e.target.value })} placeholder="np. interest" />
                                    </Field>
                                    <RemoveButton onClick={() => removeCustomField(i)} />
                                </Box>
                            ))}
                            <AddButton label="Dodaj pole niestandardowe" onClick={addCustomField} />
                        </VStack>
                    </Box>
                </VStack>
            </CollapsibleSection>

            {hasRealColumns && (
                <CollapsibleSection title="Kolumna decyzyjna i statusy" subtitle="Które wiersze arkusza kwalifikują się do importu, jak mapować ich statusy" defaultOpen forceOpen={forceOpen}>
                    <VStack align="stretch" gap={4}>
                        <Field label="Kolumna decyzyjna" hint='np. "Stan" — importowane będą tylko wiersze z wybraną poniżej wartością'>
                            <FormSelect
                                value={leadSource.decisionColumn ?? ""}
                                onChange={setDecisionColumn}
                                options={columns.map(col => ({ value: col, label: col }))}
                                placeholder="— nie filtruj —"
                            />
                        </Field>
                        {leadSource.decisionColumn && (
                            <Field label="Wartości kwalifikujące do importu" hint="wybierz z podpowiedzi albo wpisz własną wartość i naciśnij Enter">
                                <TagsInput
                                    values={leadSource.decisionColumnAllowedValues ?? []}
                                    onChange={(v) => setLeadSource({ decisionColumnAllowedValues: v })}
                                    suggestions={decisionColumnValues}
                                    placeholder="np. Zainteresowany"
                                />
                                {isLoadingDecisionValues && (
                                    <HStack mt={1} gap={2}><Spinner size="xs" /><Text fontSize="xs" color="text.grey">Wczytywanie wartości z arkusza…</Text></HStack>
                                )}
                            </Field>
                        )}

                        <Field label="Kolumna statusu" hint='np. "Status" — wartości z tej kolumny zostaną zmapowane na nasze statusy przy imporcie'>
                            <FormSelect
                                value={leadSource.statusColumn ?? ""}
                                onChange={setStatusColumn}
                                options={columns.map(col => ({ value: col, label: col }))}
                                placeholder="— nie mapuj —"
                            />
                        </Field>
                        {leadSource.statusColumn && (
                            <>
                                {isLoadingStatusValues && (
                                    <HStack gap={2}><Spinner size="xs" /><Text fontSize="xs" color="text.grey">Wczytywanie wartości z arkusza…</Text></HStack>
                                )}
                                {statusColumnValues.map((val) => (
                                    <Box key={val} display="flex" alignItems="center" gap={4}>
                                        <Text fontSize="sm" color="text.white" flex={1} minW={0} title={val} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{val}</Text>
                                        <Box flex={1}>
                                            <FormSelect value={statusFor(val)} onChange={(v) => setStatusFor(val, v || null)} options={LEAD_STATUS_OPTIONS} placeholder="— nie mapuj —" />
                                        </Box>
                                    </Box>
                                ))}
                                <Field label="Domyślny status" hint="dla wartości bez dopasowania na liście powyżej (albo pustej komórki)">
                                    <FormSelect
                                        value={leadSource.defaultStatus ?? ""}
                                        onChange={(v) => setLeadSource({ defaultStatus: v || null })}
                                        options={LEAD_STATUS_OPTIONS}
                                        placeholder="Brak (domyślnie: New)"
                                    />
                                </Field>
                            </>
                        )}
                    </VStack>
                </CollapsibleSection>
            )}
        </VStack>
    );
};

export default Step2LeadSource;
